import React from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import { compose, withProps } from "recompose";
import { Input, Avatar, Select, Col, Row, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { Form, FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";

import Upload from "../../components/controls/Upload";
import {
  updateUserProfile,
  UPDATE_USER_PROFILE
} from "../../store/actions/user";
import { getOptimizedImage } from "../../utils/images";
import Button from "../../components/Button";
import { applicationLanguages } from "../../utils/data/static/applicationLanguages";

import styled from "styled-components/macro";

const Container = styled.div`
  width: 100%;

  .avatar-container {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    gap: 16px;
  }

  .avatar {
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const { Option } = Select;

const ProfileForm = ({ loading, form, avatar, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <Container data-test="user-profile-form">
      <Form layout="vertical" onSubmit={onSubmit}>
        <Row gutter={20} type="flex" justify="center">
          <Col xs={24} md={12}>
            <FormItem
              form={form}
              label={t("global:name")}
              name="name"
              rules={[
                { required: true, message: t("form:emptyFieldValidation") }
              ]}
              render={<Input placeholder="Worcket" />}
            />
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              form={form}
              label={t("global:email")}
              name="email"
              render={
                <Input
                  disabled
                  data-test="user-email"
                  placeholder={t("user:profile.emailPlaceholder")}
                />
              }
            />
          </Col>
        </Row>
        <Row gutter={20} type="flex" justify="center">
          <Col xs={24} md={12}>
            <div className="ant-form-item-label">
              <label title={t("global:image")}>{t("global:image")}</label>
            </div>
            <div className="avatar-container">
              <Avatar
                size={60}
                icon="user"
                src={avatar && getOptimizedImage(avatar)}
                shape="circle"
                className="avatar"
              />
              <FormItem
                form={form}
                name="avatar"
                style={{ margin: 0 }}
                render={
                  <Upload
                    fileType="image/*"
                    label={t("user:profile.selectAvatar")}
                    options={{ upload_preset: "opportunity" }}
                    action={process.env.REACT_APP_CLOUDINARY_IMAGE_URL}
                  />
                }
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              form={form}
              label={t("global:language")}
              name="locale"
              render={
                <Select>
                  {applicationLanguages.map(({ country, label }) => (
                    <Option key={country} value={country}>
                      {label}
                    </Option>
                  ))}
                </Select>
              }
            />
          </Col>
        </Row>
        <Row gutter={20} type="flex" justify="center">
          <Col xs={24} md={12}>
            <div className="ant-form-item-label">
              <label title={t("global:notifications")}>
                {t("global:notifications")}
              </label>
            </div>
            <div className="avatar-container">
              <FormItem
                form={form}
                name="notifications"
                decoratorProps={{ valuePropName: "checked" }}
                style={{ margin: 0 }}
                render={<Switch />}
              />
              <span>{t("user:profile.notifications")}</span>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <FormItem
              form={form}
              label={t("user:profile.customId")}
              name="customId"
              render={<Input placeholder="0123456789" />}
            />
          </Col>
        </Row>
        <WrapperButton>
          <Button loading={loading} type="primary" size="lg" htmlType="submit">
            {t("global:save")}
          </Button>
        </WrapperButton>
      </Form>
    </Container>
  );
};

const enhancer = compose(
  connect(
    undefined,
    { updateUserProfile }
  ),
  isLoading(UPDATE_USER_PROFILE),
  withForm({
    selector: ({ company, headquarter }, { profile }) => {
      const locale =
        profile.locale === ""
          ? company.country || headquarter.country
          : profile.locale;
      return {
        ...profile,
        locale
      };
    },
    onSubmit: (values, { updateUserProfile }) =>
      updateUserProfile(R.omit(["email", "roles"], values))
  }),
  withProps(({ form }) => ({
    avatar: form.getFieldValue("avatar")
  }))
);

export default enhancer(ProfileForm);
